/**
 * Maps a campaign task `stateReason` code (from the backend when a social task is
 * REJECTED) to human-readable copy, ported from influenster-v4-mobile
 * (`parseErrorCode`). The `design` flag mirrors mobile: `V1` reasons are shown
 * inline; `V2` reasons are generic content-requirement failures.
 */
import { getTaskDisplay } from './taskTypes';

export type RejectionDesign = 'V1' | 'V2';

export interface TaskRejectionReason {
  /** Human-readable explanation of why the post was rejected. */
  message: string;
  design: RejectionDesign;
}

const MISSING_KEYWORDS_PREFIX = 'MISSING_KEYWORDS: ';

export const GENERIC_REJECTION_MESSAGE = 'Your post has been rejected.';

const V2_MESSAGES: Record<string, string> = {
  DID_NOT_MEET_PROGRAM_REQUIREMENT: "Your post did not meet the program's content requirements.",
  INCLUDES_SCREENSHOT: 'Your post contains a screenshot.',
  AI_GENERATED: 'Your post contains AI content.',
  INAPPROPRIATE_MEDIA: 'Your post contains inappropriate content.',
  PRODUCT_NOT_FEATURED: 'Your post does not contain the product.',
  PRODUCT_NOT_UNBOXED_NOR_IN_USE: "Your post doesn't show the product unboxed or in use.",
  UNRELATED_TO_POST: 'Your post includes unrelated content.',
  ISSUE_WITH_CAPTION: "Your post's caption did not meet the program's content requirements.",
  DUPLICATE_CONTENT: 'Your post appears to feature duplicate content.',
  POST_WITH_KEYWORDS_ALREADY_REJECTED: "Your post did not meet the program's content requirements.",
};

export interface TaskRejectionModalCopy {
  title: string;
  description: string;
}

const MODAL_TITLES: Record<string, string> = {
  MISSING_SOCIAL_CONNECTION: 'Connect your account.',
  MEDIA_SUBMISSION_FAILED: 'We couldn’t process your media!',
  SOCIAL_TASK_DELETED_BEFORE_EXPIRY: 'We can’t find your post anymore!',
  POST_CREATED_BEFORE_CAMPAIGN_START: 'Please create a new post.',
  POST_HAS_BEEN_USED_TO_COMPLETE_ANOTHER_TASK: 'Please create a new post.',
};

export function getTaskRejectionModalCopy(
  stateReason: string | null | undefined,
  taskType: string
): TaskRejectionModalCopy {
  if (stateReason === 'NO_POSTS_FOUND') {
    return {
      title: 'We can’t find your post!',
      description: `Make sure to use your linked ${getTaskDisplay(taskType).platform} account and ensure hashtags are spelled correctly, as they are case sensitive.`,
    };
  }

  const reason = stateReason ?? '';
  const title = reason.startsWith(MISSING_KEYWORDS_PREFIX)
    ? 'Required tags are missing.'
    : Object.prototype.hasOwnProperty.call(MODAL_TITLES, reason)
      ? MODAL_TITLES[reason]
      : 'We couldn’t verify your post.';

  return { title, description: parseTaskRejectionReason(stateReason, taskType).message };
}

/**
 * Parse a task rejection `stateReason` into display copy.
 * @param stateReason raw code from the backend (may be null/empty)
 * @param taskType the campaign task type (used to name the social provider)
 */
export function parseTaskRejectionReason(
  stateReason: string | null | undefined,
  taskType: string
): TaskRejectionReason {
  const reason = stateReason ?? '';
  const provider = getTaskDisplay(taskType).platform;

  if (reason === 'NO_POSTS_FOUND') {
    return {
      message: `We can't find your post! Make sure to use your linked ${provider} account and ensure hashtags are spelled correctly, as they are case sensitive.`,
      design: 'V1',
    };
  }
  if (reason === 'MISSING_SOCIAL_CONNECTION') {
    return {
      message: `Your ${provider} account is not connected to Influenster. Connect via settings to complete this task.`,
      design: 'V1',
    };
  }
  if (reason === 'MEDIA_SUBMISSION_FAILED') {
    return {
      message: "Uh-oh! We weren't able to process your media! Please submit it again.",
      design: 'V1',
    };
  }
  if (reason.startsWith(MISSING_KEYWORDS_PREFIX)) {
    const tags = reason.slice(MISSING_KEYWORDS_PREFIX.length);
    return {
      message: `Missing tags: ${tags}. Make sure to tag your post with the correct hashtags.`,
      design: 'V1',
    };
  }
  if (reason === 'SOCIAL_TASK_DELETED_BEFORE_EXPIRY') {
    return {
      message:
        "Oh no! We can't find your post anymore. Please post it again and keep it in your feed.",
      design: 'V1',
    };
  }
  if (reason === 'POST_CREATED_BEFORE_CAMPAIGN_START') {
    return {
      message:
        'You used an old post for this task! Please create a new post with the required tags.',
      design: 'V1',
    };
  }
  if (reason === 'POST_HAS_BEEN_USED_TO_COMPLETE_ANOTHER_TASK') {
    return {
      message:
        'Please create a new post for each program. Creating one post for multiple programs is prohibited.',
      design: 'V1',
    };
  }

  if (Object.prototype.hasOwnProperty.call(V2_MESSAGES, reason)) {
    return { message: V2_MESSAGES[reason], design: 'V2' };
  }

  return { message: GENERIC_REJECTION_MESSAGE, design: 'V2' };
}
